import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router';
import { handleRequestHelper } from '../utils/helpers';
import { useNotifications } from '../Providers/NotificationsProvider/NotificationsProvider';
import api from '../api/api';

const useOrganizers = () => {
  const { producerId } = useParams();
  const { showNotification } = useNotifications();
  const [organizers, setOrganizers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const getProducerOrganizers = useCallback(async () => {
    if (!producerId) return;

    setIsLoading(true);
    await handleRequestHelper({
      endpoint: () => api.getProducerOrganizers(producerId),
      showNotification,
      onSuccess: ({ organizers }) => {
        setOrganizers(organizers);
      },
      onFailure: ({ errorMessage }) => {
        console.error('Error fetching organizers:', errorMessage);
        setOrganizers([]);
      },
    });
    setIsLoading(false);
  }, [producerId, showNotification]);

  const searchProducers = useCallback(
    async (searchQuery) => {
      if (!searchQuery) {
        return [];
      }

      let organizers = [];

      await handleRequestHelper({
        endpoint: () => api.getAllProducers({ search: searchQuery, limit: 10 }),
        showNotification,
        onSuccess: ({ producers: searchResults }) => {
          // Filter out organizers that are already associated with this producer
          const currentOrganizerIds = organizers.map((org) => org.organizer?.id || org.organizerId);
          organizers = (searchResults || []).filter(
            (producer) => !currentOrganizerIds.includes(producer.id)
          );
        },
        onFailure: ({ errorMessage }) => {
          console.error('Error searching organizers:', errorMessage);
          organizers = [];
        },
      });

      return organizers;
    },
    [showNotification, organizers]
  );

  const handleOrganizerSelect = useCallback(
    async (organizer) => {
      if (!organizer || !producerId) return;

      await handleRequestHelper({
        endpoint: () => api.addOrganizerToProducer(organizer.id, producerId),
        showNotification,
        onSuccess: () => {
          showNotification('Organizador agregado exitosamente', 'success');
          getProducerOrganizers(); // Refresh the list
        },
        onFailure: ({ errorMessage }) => {
          console.error('Error adding organizer:', errorMessage);
          showNotification('Error al agregar organizador', 'error');
        },
      });
    },
    [producerId, showNotification, getProducerOrganizers]
  );

  const removeOrganizerFromProducer = useCallback(
    async (organizerId) => {
      if (!producerId || !organizerId) return;

      await handleRequestHelper({
        endpoint: () => api.removeOrganizerFromProducer(organizerId, producerId),
        showNotification,
        onSuccess: () => {
          showNotification('Organizador removido exitosamente', 'success');
          getProducerOrganizers(); // Refresh the list
        },
        onFailure: ({ errorMessage }) => {
          console.error('Error removing organizer:', errorMessage);
          showNotification('Error al remover organizador', 'error');
        },
      });
    },
    [producerId, showNotification, getProducerOrganizers]
  );

  useEffect(() => {
    getProducerOrganizers();
  }, [getProducerOrganizers]);

  return {
    organizers,
    isLoading,
    getProducerOrganizers,
    searchProducers,
    handleOrganizerSelect,
    removeOrganizerFromProducer,
  };
};

export default useOrganizers;
